import React from "react";
import { mergeStrings } from "../../lib/helpers";
import styled from "styled-components";
import useTheme from "../../lib/useTheme";

interface DGA_ChipProps extends React.HTMLAttributes<HTMLParagraphElement> {
  size?: "small" | "large";
  whiteBackground?: boolean;
  title?: string;
  description?: string;
  authorDetails?: {
    name?: string;
    title?: string;
    avatar?: any;
  };
}

const Quote: React.FC<DGA_ChipProps> = ({
  size,
  whiteBackground = true,
  title,
  description,
  authorDetails,
  ...props
}) => {
  const theme = useTheme();

  return (
    <StyledComponent
      $theme={theme}
      className={mergeStrings(
        "dgaui dgaui_quote" +
          (whiteBackground ? " whiteBackground" : "") +
          (!authorDetails ? " noAuthorDetails" : "") +
          ` ${theme.direction} ${size ?? ""}`,
        props.className
      )}
      {...props}
    >
      <div className="dgaui_quoteTextContainer">
        <span className="dgaui_quoteTitleQuote start">”</span>
        <span className="dgaui_quoteTitleText">{title}</span>
        <span className="dgaui_quoteTitleQuote end">“</span>
        {description && <div className="dgaui_quoteDescription">{description}</div>}
      </div>

      {authorDetails && (
        <div className="dgaui_quoteAuthorDetailsContainer">
          {authorDetails.avatar && (
            <div className="dgaui_quoteAuthorDetailsAvatar">{authorDetails.avatar}</div>
          )}
          <div className="dgaui_quoteAuthorDetailsText">
            <div className="dgaui_quoteAuthorDetailsName">{authorDetails.name}</div>
            <div className="dgaui_quoteAuthorDetailsTitle">{authorDetails.title}</div>
          </div>
        </div>
      )}
    </StyledComponent>
  );
};

const StyledComponent = styled.p<{
  $theme: Theme;
}>`
  direction: ${(p) => p.$theme.direction};
  border-radius: 16px;
  padding: 20px;

  &.small {
    width: 640px;
  }
  &.large {
    width: 846px;
  }

  &.whiteBackground {
    background-color: ${(p) => p.$theme.palette.neutral[50]};
  }

  .dgaui_quoteTextContainer {
    position: relative;
    color: ${(p) => p.$theme.textColor};

    margin-inline-start: 64px;

    .dgaui_quoteDescription {
      line-height: 30px;
      color: ${(p) => p.$theme.palette.neutral[700]};
      font-weight: 400;
      font-size: 20px;
      margin-top: 16px;
    }
    .dgaui_quoteTitleText {
      font-size: 24px;
      line-height: 32px;
      font-weight: 500;
    }
    .dgaui_quoteTitleQuote {
      color: ${(p) => p.$theme.palette.primary[800]};
      display: block;
      transform: rotateY(180deg);
      position: absolute;

      &.start {
        line-height: 0;
        font-size: 128px;
        top: 45px;
        inset-inline-start: -75px;
      }
      &.end {
        line-height: 0;
        font-size: 128px;
        bottom: -56px;
        inset-inline-end: 0;
      }
    }
  }

  .dgaui_quoteAuthorDetailsContainer {
    margin-top: 48px;
    display: flex;
    align-items: center;

    .dgaui_quoteAuthorDetailsAvatar {
      margin-inline-end: 8px;
    }
    .dgaui_quoteAuthorDetailsText {
      .dgaui_quoteAuthorDetailsName {
        font-size: 20px;
      }
      .dgaui_quoteAuthorDetailsTitle {
        color: ${(p) => p.$theme.textColor};
        margin-top: 8px;
        color: ${(p) => p.$theme.palette.neutral[700]};
      }
    }
  }

  &.noAuthorDetails {
    .dgaui_quoteTextContainer {
      .dgaui_quoteDescription {
        margin-inline-end: 64px;
      }
    }

    .dgaui_quoteTitleQuote {
      &.end {
        bottom: 20%;
      }
    }
  }

  &.rtl {
    .dgaui_quoteTextContainer {
      .dgaui_quoteTitleQuote {
        transform: rotateY(0deg);
      }
    }
  }
`;

export default Quote;
